import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const EVENT_COLORS = {
  dropoff: { bg: 'bg-[#C6E76C]', text: 'text-[#474747]', border: 'border-[#C6E76C]' },
  inspection: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  payout_reminder: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  internal_task: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  follow_up: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' }
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Calendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(location.state?.view || 'month'); // month, week, day, agenda
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  const [newBooking, setNewBooking] = useState({
    title: '',
    type: 'dropoff',
    start_time: '',
    end_time: '',
    location: '',
    notes: ''
  });

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      // Pad range by 7 days each side so week views spanning month edges work
      const startDate = new Date(new Date(year, month, 1).getTime() - 7 * 86400000).toISOString();
      const endDate = new Date(new Date(year, month + 1, 0).getTime() + 7 * 86400000).toISOString();

      const response = await axios.get(`${API}/bookings`, {
        params: {
          start_date: startDate,
          end_date: endDate
        },
        withCredentials: true
      });
      
      setBookings(response.data);
    } catch (error) {
      // Error logged: 'Failed to load bookings:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const targetDate = new Date(year, month, day);
    const targetDateStr = targetDate.toISOString().split('T')[0];
    
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.start_time).toISOString().split('T')[0];
      return bookingDate === targetDateStr;
    });
  };

  const getEventsForDate = (dateObj) => {
    const targetDateStr = dateObj.toISOString().split('T')[0];
    return bookings
      .filter(b => new Date(b.start_time).toISOString().split('T')[0] === targetDateStr)
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * 86400000));
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handlePrevMonth = () => {
    if (view === 'week') {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 86400000));
    } else if (view === 'day') {
      setCurrentDate(new Date(currentDate.getTime() - 86400000));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (view === 'week') {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 86400000));
    } else if (view === 'day') {
      setCurrentDate(new Date(currentDate.getTime() + 86400000));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bookings`, newBooking, { withCredentials: true });
      toast.success('Booking created');
      setCreateModalOpen(false);
      setNewBooking({
        title: '',
        type: 'dropoff',
        start_time: '',
        end_time: '',
        location: '',
        notes: ''
      });
      loadBookings();
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const selectedDateTime = day instanceof Date
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0)
      : new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 9, 0); // Default to 9 AM
    
    setSelectedDate(selectedDateTime);
    setNewBooking({
      ...newBooking,
      start_time: selectedDateTime.toISOString(),
      end_time: new Date(selectedDateTime.getTime() + 60*60*1000).toISOString() // +1 hour
    });
    setCreateModalOpen(true);
  };

  const handleEventClick = (booking) => {
    if (booking.application_id) {
      navigate(`/application/${booking.application_id}`);
    } else if (booking.item_id) {
      navigate(`/item/${booking.item_id}`);
    } else if (booking.seller_id) {
      navigate(`/seller/${booking.seller_id}`);
    }
  };

  const days = getDaysInMonth();
  const today = new Date();
  const isToday = (day) => {
    return day &&
      today.getFullYear() === currentDate.getFullYear() &&
      today.getMonth() === currentDate.getMonth() &&
      today.getDate() === day;
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-heading font-semibold text-[#474747]">
                {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevMonth}
                  className="p-2 rounded-md hover:bg-gray-100 border border-[#E9E9E9]"
                >
                  <ChevronLeft className="w-5 h-5 text-[#474747]" />
                </button>
                <button
                  onClick={handleToday}
                  className="px-4 py-2 rounded-md hover:bg-gray-100 border border-[#E9E9E9] text-sm font-medium text-[#474747]"
                >
                  Today
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-2 rounded-md hover:bg-gray-100 border border-[#E9E9E9]"
                >
                  <ChevronRight className="w-5 h-5 text-[#474747]" />
                </button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setView('month')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'month'
                    ? 'bg-[#C6E76C] text-[#474747]'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'week'
                    ? 'bg-[#C6E76C] text-[#474747]'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setView('day')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'day'
                    ? 'bg-[#C6E76C] text-[#474747]'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
                }`}
              >
                Day
              </button>
              <button
                onClick={() => setView('agenda')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'agenda'
                    ? 'bg-[#C6E76C] text-[#474747]'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
                }`}
              >
                Agenda
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Calendar Grid */}
      <div className="px-6 py-8">
        {/* Legend */}
        <div className="flex items-center gap-6 mb-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#C6E76C]"></div>
            <span className="text-gray-600">Drop-offs</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-400"></div>
            <span className="text-gray-600">Inspections</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-400"></div>
            <span className="text-gray-600">Payout Reminders</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-400"></div>
            <span className="text-gray-600">Internal Tasks</span>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-[#E9E9E9] p-12 text-center">
            <p className="text-gray-500">Loading calendar...</p>
          </div>
        ) : view === 'month' ? (
          <div className="bg-white rounded-xl border border-[#E9E9E9] overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-[#E9E9E9]">
              {DAY_NAMES.map(day => (
                <div key={day} className="p-4 text-center text-sm font-semibold text-[#474747] border-r border-[#E9E9E9] last:border-r-0">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7">
              {days.map((day, idx) => {
                const events = getEventsForDay(day);
                const isTodayCell = isToday(day);
                
                return (
                  <div
                    key={day ? `day-${day}` : `pad-${idx}`}
                    onClick={() => handleDayClick(day)}
                    className={`min-h-[120px] p-2 border-r border-b border-[#E9E9E9] last:border-r-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !day ? 'bg-gray-50' : ''
                    } ${isTodayCell ? 'bg-[#C6E76C]/10' : ''}`}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-semibold mb-2 ${
                          isTodayCell ? 'text-[#474747] bg-[#C6E76C] rounded-full w-6 h-6 flex items-center justify-center' : 'text-gray-700'
                        }`}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {events.slice(0, 3).map(event => {
                            const colors = EVENT_COLORS[event.type] || EVENT_COLORS.follow_up;
                            return (
                              <div
                                key={event.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEventClick(event);
                                }}
                                className={`text-xs p-1.5 rounded ${colors.bg} ${colors.text} border ${colors.border} cursor-pointer hover:opacity-80 transition-opacity`}
                              >
                                <div className="font-medium truncate">{formatTime(event.start_time)}</div>
                                <div className="truncate">{event.title}</div>
                              </div>
                            );
                          })}
                          {events.length > 3 && (
                            <div className="text-xs text-gray-500 font-medium">
                              +{events.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === 'week' ? (
          <div className="bg-white rounded-xl border border-[#E9E9E9] overflow-hidden" data-testid="calendar-week-view">
            <div className="grid grid-cols-7 border-b border-[#E9E9E9]">
              {getWeekDays().map(d => (
                <div key={d.toISOString()} className={`p-4 text-center border-r border-[#E9E9E9] last:border-r-0 ${d.toDateString() === new Date().toDateString() ? 'bg-[#C6E76C]/10' : ''}`}>
                  <p className="text-xs text-gray-500">{DAY_NAMES[d.getDay()]}</p>
                  <p className="text-sm font-semibold text-[#474747]">{d.getDate()} {MONTH_NAMES[d.getMonth()].slice(0, 3)}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {getWeekDays().map(d => {
                const events = getEventsForDate(d);
                return (
                  <div
                    key={`col-${d.toISOString()}`}
                    onClick={() => handleDayClick(d)}
                    className="min-h-[300px] p-2 border-r border-[#E9E9E9] last:border-r-0 space-y-1 cursor-pointer hover:bg-gray-50"
                  >
                    {events.map(event => {
                      const colors = EVENT_COLORS[event.type] || EVENT_COLORS.follow_up;
                      return (
                        <div
                          key={event.id}
                          onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                          className={`text-xs p-1.5 rounded ${colors.bg} ${colors.text} border ${colors.border} cursor-pointer hover:opacity-80`}
                        >
                          <div className="font-medium truncate">{formatTime(event.start_time)}</div>
                          <div className="truncate">{event.title}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === 'day' ? (
          <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="calendar-day-view">
            <h3 className="text-lg font-semibold text-[#474747] mb-4">
              {currentDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
            {getEventsForDate(currentDate).length === 0 ? (
              <p className="text-sm text-gray-500">No bookings on this day. Click a day in Month view to create one.</p>
            ) : (
              <div className="space-y-3">
                {getEventsForDate(currentDate).map(event => {
                  const colors = EVENT_COLORS[event.type] || EVENT_COLORS.follow_up;
                  return (
                    <div
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className={`p-4 rounded-lg ${colors.bg} ${colors.text} border ${colors.border} cursor-pointer hover:opacity-80`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{event.title}</p>
                        <p className="text-sm">{formatTime(event.start_time)} – {formatTime(event.end_time)}</p>
                      </div>
                      {event.location && <p className="text-sm mt-1">📍 {event.location}</p>}
                      {event.notes && <p className="text-sm mt-1 opacity-80">{event.notes}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="calendar-agenda-view">
            <h3 className="text-lg font-semibold text-[#474747] mb-4">Agenda — {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
            {bookings.length === 0 ? (
              <p className="text-sm text-gray-500">No bookings scheduled this month.</p>
            ) : (
              <div className="divide-y divide-[#E9E9E9]">
                {[...bookings].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)).map(event => {
                  const colors = EVENT_COLORS[event.type] || EVENT_COLORS.follow_up;
                  return (
                    <div
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className="py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-50 px-2 rounded"
                    >
                      <div className="text-center w-16 flex-shrink-0">
                        <p className="text-xs text-gray-500">{new Date(event.start_time).toLocaleDateString('en-AU', { month: 'short' })}</p>
                        <p className="text-xl font-semibold text-[#474747]">{new Date(event.start_time).getDate()}</p>
                      </div>
                      <div className={`w-1.5 self-stretch rounded ${colors.bg}`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#474747] truncate">{event.title}</p>
                        <p className="text-xs text-gray-500">{formatTime(event.start_time)} – {formatTime(event.end_time)}{event.location ? ` · ${event.location}` : ''}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize flex-shrink-0">{(event.type || '').replace('_', ' ')}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Booking Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">New Booking</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBooking} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Type *</label>
              <select
                required
                value={newBooking.type}
                onChange={(e) => setNewBooking({...newBooking, type: e.target.value})}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
              >
                <option value="dropoff">Drop-off</option>
                <option value="inspection">Inspection</option>
                <option value="payout_reminder">Payout Reminder</option>
                <option value="internal_task">Internal Task</option>
                <option value="follow_up">Follow-up</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Title *</label>
              <input
                type="text"
                required
                value={newBooking.title}
                onChange={(e) => setNewBooking({...newBooking, title: e.target.value})}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                placeholder="e.g., Drop-off with Sarah Johnson"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">Start Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={newBooking.start_time ? new Date(newBooking.start_time).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setNewBooking({...newBooking, start_time: new Date(e.target.value).toISOString()})}
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">End Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={newBooking.end_time ? new Date(newBooking.end_time).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setNewBooking({...newBooking, end_time: new Date(e.target.value).toISOString()})}
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Location</label>
              <input
                type="text"
                value={newBooking.location}
                onChange={(e) => setNewBooking({...newBooking, location: e.target.value})}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                placeholder="e.g., Hope Valley WA"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Notes</label>
              <textarea
                value={newBooking.notes}
                onChange={(e) => setNewBooking({...newBooking, notes: e.target.value})}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                rows="3"
                placeholder="Additional details..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold">
                Create Booking
              </Button>
              <Button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                variant="outline"
                className="border-[#E9E9E9]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
